import locale from "csi/locale";
import csi from "csi/main";
import reflectionHints from "csi/reflectionHints";
import { getAncestorNodes } from "csi/service/nodeService";
import { rootCache } from "csi/state/Cache";
import { browserStore, RootStore } from "csi/state/rootStore";
import { C8Node, C8NodeType, cnl } from "csi/typings";
import { convertObjectToHash, decomposeHash, LOCATION_COMPARE_URL, LOCATION_DISABLE_TAB_MATCHING, LOCATION_PREVIOUS_URL, LOCATION_REVISION_URL, LOCATION_RIGHT_PANE, LOCATION_RIGHT_PANE_URL, LOCATION_SECTION, LOCATION_SECTION_ITEM, LOCATION_TAB } from "csi/utility/routerUtility";
import { stringCombine } from "csi/utility/stringUtility";
import xml from "csi/xml";
import { action, computed, makeObservable, observable } from "mobx";
import { FunctionComponent } from "react";
import { ANGLE_SEPARATOR, DASH_SEPARATOR } from "./viewUtils";

type ReactSlotProps = Record<string, any>;
type ReactComponentReference = string | FunctionComponent | undefined;

/** Resolve the given function component reference */
function resolveComponentReference(componentReference: ReactComponentReference): FunctionComponent | undefined {
	if (typeof componentReference === "string") {
		return csi.syncRequire(componentReference)?.default;
	}

	return componentReference;
}

export interface ReactSlot {
	component: FunctionComponent;
	properties?: ReactSlotProps;
}

export default class RouterStore {
	private _rootStore: RootStore;

	@observable.ref private _breadcrumbUrls: cnl[];
	@observable.ref private _linkedNodeUrl: cnl;
	@observable private _breadcrumbCtr = 0;
	@observable private _helpTitle: string;
	@observable private _helpType: string;
	@observable private _helpView: string;
	@observable private _refreshTimestamp = 0;
	@observable private _locationIsDormant: boolean = false;
	@observable private _previousLocationUrl: cnl = "";

	@observable.ref private _bannerComponent?: ReactSlot;
	@observable.ref private _bodyComponent?: ReactSlot;
	@observable.ref private _inlineComponent?: ReactSlot;
	@observable.ref private _centerFloatComponent?: ReactSlot;
	@observable.ref private _featureComponent?: ReactSlot;

	/** object tracking actual (resolved) parameter values for URL (actuals could be different than targets) */
	@observable.struct private _actualLocationValues: Record<string, string> = {};

	/** object tracking target parameter values captured from browser URL  */
	@observable.struct private _targetLocationValues: Record<string, string> = {};

	/** indicates if breadcrumb view should be hidden for current route */
	@observable hideBreadcrumb = false;

	/** indicates if tabs should be hidden for current route */
	@observable hideTabs = false;

	/** url of node that serves as context to current location node */
	@observable locationContextUrl: cnl = "";

	/** title for data setup item */
	@observable locationTitleForDataSetup = "";

	/** type of current node in breadcrumb */
	@observable locationType = "";

	/** URL of revision of current node in breadcrumb */
	@observable locationRevisionUrl: cnl = "";

	/** URL of current node in breadcrumb */
	@observable locationUrl: cnl = "";

	/** indicates if breadcrumb view should be rendered for current route */
	@observable renderBreadcrumb = false;

	/** indicates if current route belongs to data setup */
	@observable renderDataSetup: boolean;

	/** indicates if current route belongs to an item instance within data setup */
	@observable renderDataSetupInstance = false;

	/** name of selected tab */
	@observable tabName = "";

	/** if true then track url outside of data setup when first entering data setup */
	trackDataSetupReturnUrl = false;

	/** the resolved location used to update browser hash */
	@computed get actualRoute(): string {
		return convertObjectToHash(this._actualLocationValues);
	}

	/** the banner component, would show up underneath breadcrumb */
	get bannerComponent(): ReactSlot | undefined {
		return this._bannerComponent;
	}

	/** React component to render under document body, such as dialog */
	get bodyComponent(): ReactSlot | undefined {
		return this._bodyComponent;
	}

	/** counter used to force update to breadcrumb (temporary until all state for breadcrumb is in MobX) */
	get breadcrumbCtr(): number {
		return this._breadcrumbCtr;
	}

	/** urls of nodes to display in the breadcrumb */
	get breadcrumbUrls(): cnl[] {
		return this._breadcrumbUrls;
	}

	/** component to render over normal page content */
	get centerFloatComponent(): ReactSlot | undefined {
		return this._centerFloatComponent;
	}

	/** compare url in browser hash */
	get compareRevisionUrl(): string {
		return this._targetLocationValues[LOCATION_COMPARE_URL];
	}

	/** return true if previous tab should not be considered when navigating to node (default to first tab) */
	get disableTabMatching(): boolean {
		return !!this._targetLocationValues[LOCATION_DISABLE_TAB_MATCHING];
	}

	/** React component to render in main page content */
	get featureComponent(): ReactSlot | undefined {
		return this._featureComponent;
	}

	/** title for help pane */
	get helpTitle(): string {
		return this._helpTitle;
	}

	/** node type owning view for which help pane content is displayed */
	get helpType(): string {
		return this._helpType;
	}

	/** view id for which help pane content is displayed */
	get helpView(): string {
		return this._helpView;
	}

	/** get the refreshTimestamp so that Dojo view will be refreshed */
	get refreshTimestamp(): number {
		return this._refreshTimestamp;
	}

	/** React component to render under document body, such as dialog */
	get inlineComponent(): ReactSlot | undefined {
		return this._inlineComponent;
	}

	/** C8 node of the linked node to display in the breadcrumb */
	get linkedNode(): C8Node {
		return rootCache.get(this.linkedNodeUrl);
	}

	/** url of the linked node to display in the breadcrumb */
	get linkedNodeUrl(): cnl {
		return this._linkedNodeUrl;
	}

	/**
	 * This calls getAncestorNodes with hard set parameters of the locationUrl and rootCache to prevent react components
	 * from using the caching directly
	 */
	get locationAncestorNodes(): C8Node[] {
		return getAncestorNodes(this.locationUrl, rootCache);
	}

	/** description for current location to be used as default description for favorite */
	@computed get locationDescription(): string {
		return this.getLocationTitle(DASH_SEPARATOR);
	}

	/** text of location node in breadcrumb */
	@computed get locationName(): string {
		return this.getNodeLocationName(this.locationUrl);
	}

	/** indicates if current node in breadcrumb is dormant (i.e. even actions not requiring rights should be disabled) */
	get locationIsDormant(): boolean {
		return this._locationIsDormant;
	}

	/** current location node */
	@computed get locationNode(): C8Node {
		return rootCache.get(this.locationUrl);
	}

	/** list of nodes in breadcrumb trail (current node is last) */
	@computed get locationNodes(): C8Node[] {
		return rootCache.getNodes(this.breadcrumbUrls);
	}

	/** indicates if current location is revisable node where the state can change (not a template) */
	@computed get locationSupportsStateActions(): boolean {
		const { locationRevisionNode, locationNode } = this;
		return locationRevisionNode && reflectionHints.isRevisableNodeWithState(locationNode);
	}

	/** title for current location to be used as browser document title */
	@computed get locationTitle(): string {
		return this.getLocationTitle(ANGLE_SEPARATOR);
	}

	/** string detailing path of nodes in breadcrumb */
	@computed get locationTrail(): string {
		const names = this.locationNodes.map((node) => locale.getNodeConfiguredName(node, rootCache));
		return names.join(ANGLE_SEPARATOR);
	}

	/** localized type name of current node in breadcrumb */
	@computed get locationTypeName(): string {
		return locale.type(this.locationType);
	}

	/** current location revision node */
	@computed get locationRevisionNode(): C8Node {
		return rootCache.get(this.locationRevisionUrl);
	}

	/** URL of previous location node */
	get previousLocationUrl(): cnl {
		return this._previousLocationUrl;
	}

	/** revision url in browser hash */
	get revisionUrl(): cnl {
		return this._targetLocationValues[LOCATION_REVISION_URL];
	}

	/** return whether or not to show breadcrumb */
	get showBreadcrumb(): boolean {
		return this.renderBreadcrumb && !this.hideBreadcrumb;
	}

	/** return previous url parameter from browser URL */
	get targetPreviousUrl(): cnl {
		// TODO: Change to ProofURL?
		return this._targetLocationValues[LOCATION_PREVIOUS_URL];
	}

	/** return right pane targeted in browser URL */
	get targetRightPane(): string {
		return this._targetLocationValues[LOCATION_RIGHT_PANE];
	}

	/** return right pane instance (such as comment) targeted in browser URL */
	get targetRightPaneUrl(): cnl {
		return this._targetLocationValues[LOCATION_RIGHT_PANE_URL];
	}

	/** return target route last captured from browser URL */
	@computed get targetRoute(): string {
		return convertObjectToHash(this._targetLocationValues);
	}

	/** return target data setup section in browser URL */
	get targetSection(): string {
		return this._targetLocationValues[LOCATION_SECTION];
	}

	/** return target data setup section item in browser URL */
	get targetSectionItem(): string {
		return this._targetLocationValues[LOCATION_SECTION_ITEM];
	}

	/** return tab targeted in browser URL */
	get targetTab(): string {
		return this._targetLocationValues[LOCATION_TAB];
	}

	/** update current hash location */
	@action captureLocation = (): void => {
		this._targetLocationValues = decomposeHash();
	};

	/** open dialog with given component and properties */
	@action openDialog = (componentReference: ReactComponentReference, properties: ReactSlotProps = {}): VoidFunction => {
		// TODO: can we take ReactNode rather than component/props? better IDE support for code completion
		const { onClose } = properties;

		this.setBodyComponent(componentReference, {
			...properties,
			open: true,
			onClose: (event, reason) => {
				onClose?.(event, reason);
				this.setBodyComponent(null);
			},
		});

		return this.closeDialog;
	};

	/** open inline dialog with given component and properties, returning function to close it */
	@action openInlineDialog = (componentReference: ReactComponentReference, componentProperties: ReactSlotProps = {}): VoidFunction => {
		const component = resolveComponentReference(componentReference);
		const properties = {
			...componentProperties,
			open: true,
			onClose: action((event, reason) => {
				componentProperties.onClose?.(event, reason);
				this._inlineComponent = null;
			}),
		};

		this._inlineComponent = component ? { component, properties } : null;

		return this.closeInlineDialog;
	};

	/** close dialog with given component */
	@action closeDialog = (): void => {
		this._bodyComponent?.properties?.onClose?.();
	};
	
	/** close center flow component dialog */
	@action closeCenterFloatComponentDialog = (): void => {
		this._centerFloatComponent = null;
	};

	/** close inline dialog with given component */
	@action closeInlineDialog = (): void => {
		this._inlineComponent?.properties.onClose();
	};

	/** return title for current location using given separator between active tabs */
	getLocationTitle = (tabSeparator: string): string => {
		const { locationContextUrl,
			locationName,
			locationNode,
			locationRevisionNode,
			locationTitleForDataSetup,
			renderDataSetup,
			renderDataSetupInstance,
			tabName,
		} = this;

		const { fullScreenCompositeViewName } = browserStore;
		let title: string;

		if (renderDataSetup && !renderDataSetupInstance) {
			title = locationTitleForDataSetup;
		} else {
			// Pattern: "context: name - revision > tab1 > tab2"
			const contextName = this.getNodeLocationName(locationContextUrl);
			const revisionName = reflectionHints.isTemplateNode(locationNode) ? "" : locale.getRevisionNodeName(locationNode, locationRevisionNode, rootCache);

			// construct path to active tab
			const pathNames = [tabName];

			if (fullScreenCompositeViewName) {
				pathNames.push(fullScreenCompositeViewName);
			}

			title = stringCombine(contextName, locationName, ": ");
			title = stringCombine(title, revisionName, DASH_SEPARATOR);
			title = stringCombine(title, pathNames.join(tabSeparator), tabSeparator);
		}

		return title;
	};

	/**
	 * Traverses through the breadcrumbs and looks for a locationNode of a given type. If that doesn't exist,
	 * then it will return null. Type parameter is required because without this one may
	 * simply use sessionStore.locationNode instead of getLocationNode
	 */
	getBreadcrumbNode = (type: C8NodeType): C8Node | null => {
		return this.locationNodes.find((node) => xml.nodeGetType(node) === type);
	};

	/** Returns the URL of the breadcrumb node of the given type */
	getBreadcrumbNodeURL = (type: C8NodeType): cnl => {
		return xml.nodeGetURL(this.getBreadcrumbNode(type));
	};

	/** return name of a location node (i.e. node in breadcrumb) */
	getNodeLocationName = (url: cnl): string => {
		const node = rootCache.get(url);
		return locale.getNodeConfiguredName(node, rootCache);
	};

	/** set banner component  */
	@action setBannerComponent = (componentReference: ReactComponentReference, properties: ReactSlotProps = {}): void => {
		const component = resolveComponentReference(componentReference);
		this._bannerComponent = component ? { component, properties } : null;
	};

	/** set React component to use in portal under body element, which would normally be used for popup dialogs, menus, etc. */
	@action setBodyComponent = (componentReference: ReactComponentReference, properties: ReactSlotProps = {}): void => {
		const component = resolveComponentReference(componentReference);
		this._bodyComponent = component ? { component, properties } : null;
	};

	/** set list of URLs to be displayed in the breadcrumb trail */
	@action setBreadcrumbUrls = (urls: cnl[]): void => {
		this._breadcrumbUrls = urls;
	};

	/** set React component to use in portal under page center float element */
	@action setCenterFloatComponent = (componentReference: ReactComponentReference, properties: ReactSlotProps = {}): VoidFunction => {
		const component = resolveComponentReference(componentReference);
		this._centerFloatComponent = component ? { component, properties } : null;
		return this.closeCenterFloatComponentDialog;
	};

	/** set React component to use in portal under page content element, which is used to provide React layout for current node (i.e. __Host__ view) */
	@action setFeatureComponent = (componentReference: ReactComponentReference, properties: ReactSlotProps = {}): void => {
		const component = resolveComponentReference(componentReference);
		this._featureComponent = component ? { component, properties } : null;
	};

	/** set properties of current view for which help content should be displayed */
	@action setHelpPaneView(title: string, type: string, view: string): void {
		this._helpTitle = title;
		this._helpType = type;
		this._helpView = view;
	}

	/** set new location node and revision */
	@action setLocation = (node: C8Node, revisionNode?: C8Node): void => {
		this.locationType = node?.$Type || "";
		this.locationRevisionUrl = revisionNode?.$URL || csi.EMPTY_URL;
		this._previousLocationUrl = this.locationUrl;
		this.locationUrl = node?.$URL || csi.EMPTY_URL;
	};

	/** set if the location is or isnt dormant */
	@action setLocationIsDormant = (locationIsDormant: boolean): void => {
		this._locationIsDormant = locationIsDormant;
	};

	/** set linked node be displayed in the breadcrumb trail if setted */
	@action setLinkedNodeUrl = (url: cnl): void => {
		this._linkedNodeUrl = url;
	};

	/** force redraw of breadcrumb view due to some state change that is not currently observed in MobX */
	@action updateBreadcrumb = (): void => {
		++this._breadcrumbCtr;
	};

	/** force redraw of host view due to some state change that is not currently observed in MobX */
	@action updateHostView = (): void => {
		// update refreshTimestamp so that Dojo view will be refreshed
		this._refreshTimestamp = new Date().getTime();

		// reset modal view as any view it references will be recreated
		// FIX: consider moving modalView from session to router store
		this._rootStore.sessionStore.modalView = null;
	};

	constructor(rootStore: RootStore) {
		makeObservable(this);
		this._rootStore = rootStore;
	}
}
